import React, { createContext, useContext, useCallback, useEffect, useMemo, useState } from 'react';
import { api, TIPOLOGIE_KEYS } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

const FALLBACK_TIPOLOGIE = TIPOLOGIE_KEYS.map(k => ({ key: k, label: k, custom: k.startsWith('Custom ') }));
const FALLBACK_TRACKING_STATUS = [
  { key: 'PRESA_IN_CARICO', label: 'Presa in carico' },
  { key: 'IN_CONSEGNA', label: 'In consegna' },
  { key: 'CONSEGNATA', label: 'Consegnata' },
  { key: 'LASCIATO_AVVISO', label: 'Lasciato avviso di giacenza' },
  { key: 'RIFIUTATA', label: 'Rifiutata dal destinatario' },
  { key: 'TRASFERITO', label: 'Destinatario trasferito' },
  { key: 'SCONOSCIUTO_CIVICO', label: 'Sconosciuto al civico' },
  { key: 'INDIRIZZO_ERRATO', label: 'Indirizzo errato' },
  { key: 'DECEDUTO', label: 'Destinatario deceduto/a' },
  { key: 'RITORNO_MITTENTE', label: 'Ritorno al mittente per termine giacenza' },
];

// localStorage cache: catalog rarely changes (only when SuperAdmin renames a
// custom tipologia), so a stale-while-revalidate pattern gives "instant UI"
// on every page load while keeping data fresh in the background.
//   • on mount: hydrate from cache synchronously → no loading state
//   • in background: GET /api/catalog → if different, update state + cache
const CACHE_KEY = 'pc_catalog_cache_v1';

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch (e) { return null; }
}

function writeCache(payload) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(payload)); } catch (e) { /* quota / private mode */ }
}

const CatalogContext = createContext(null);
export const useCatalog = () => useContext(CatalogContext) || {
  tipologie: FALLBACK_TIPOLOGIE,
  tracking_status: FALLBACK_TRACKING_STATUS,
  labelOf: (k) => k,
  reload: () => {},
  saveTypeLabel: async () => {},
};

export const CatalogProvider = ({ children }) => {
  const { user } = useAuth();
  // Hydrate state synchronously from cache so the very first render of any
  // consumer already has the previous values (no flash of fallback data).
  const cached = readCache();
  const [tipologie, setTipologie] = useState(
    (cached && Array.isArray(cached.tipologie) && cached.tipologie.length)
      ? cached.tipologie
      : FALLBACK_TIPOLOGIE,
  );
  const [trackingStatus, setTrackingStatus] = useState(
    (cached && Array.isArray(cached.tracking_status) && cached.tracking_status.length)
      ? cached.tracking_status
      : FALLBACK_TRACKING_STATUS,
  );
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    if (!user) {
      setTipologie(FALLBACK_TIPOLOGIE);
      setTrackingStatus(FALLBACK_TRACKING_STATUS);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get('/catalog');
      const nextTipologie = (Array.isArray(data?.tipologie) && data.tipologie.length) ? data.tipologie : FALLBACK_TIPOLOGIE;
      const nextTracking = (Array.isArray(data?.tracking_status) && data.tracking_status.length) ? data.tracking_status : FALLBACK_TRACKING_STATUS;
      setTipologie(nextTipologie);
      setTrackingStatus(nextTracking);
      writeCache({ tipologie: nextTipologie, tracking_status: nextTracking, updated_at: new Date().toISOString() });
    } catch (e) {
      // Keep whatever we already have (cached or fallback). Don't wipe.
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { reload(); }, [reload]);

  const labelOf = useCallback((key) => {
    const found = tipologie.find(t => t.key === key);
    return found?.label || key;
  }, [tipologie]);

  const saveTypeLabel = useCallback(async (key, label) => {
    const { data } = await api.put('/type-labels', { key, label });
    await reload();
    return data;
  }, [reload]);

  // Memoize the context value so consumers don't re-render on each parent render.
  // Without this, every render of CatalogProvider creates a fresh object reference,
  // making React Context propagate updates to every consumer unnecessarily.
  const value = useMemo(
    () => ({ tipologie, tracking_status: trackingStatus, loading, reload, labelOf, saveTypeLabel }),
    [tipologie, trackingStatus, loading, reload, labelOf, saveTypeLabel],
  );

  return (
    <CatalogContext.Provider value={value}>
      {children}
    </CatalogContext.Provider>
  );
};
